import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import toast from 'react-hot-toast';
import { Formik, FieldArray } from 'formik';
import * as yup from 'yup';
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography,
    CircularProgress, 
    TextField, 
    Button,
    Autocomplete, 
    Divider, 
    IconButton, 
    MenuItem,
    Collapse, 
    Switch, 
    FormControlLabel, 
    Chip, 
    Grid,
} from '@mui/material';
import { 
    AddCircle, 
    RemoveCircle, 
    ExpandMore, 
    ExpandLess 
} from "@mui/icons-material";
import { ArrowBackIosNew } from '@mui/icons-material';
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { enGB } from 'date-fns/locale';
import useAuthCheck from 'hooks/useAuthCheck';
import { 
    LAYOUT_HEIGHT, 
    OTP_DELAY_MINUTES, 
    EXCLUDED_FLIGHT_STATUS, 
    DIVERT_RETURN_STATUS 
} from 'state/constants';
import api from 'state/axios';
import { parseUTCDate, parseDelays } from 'state/helpers';
import CustomDatePicker from "components/CustomDatePicker";
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import TiptapEditor from 'components/TiptapEditor';


const ADAPTER_LOCALE = enGB;

const checkoutSchema = yup.object().shape({
    reportDate: yup.date().required('Required'),
    shift: yup.object().nullable().required('Required'),
    title: yup.string().required('Required'),
    content: yup.string().required('Required'),
    todos: yup.array().of(
        yup.object().shape({
            todo: yup.string().required('Todo is required'),
            status: yup.string().required('Status is required'),
            performer: yup
                .object()
                .nullable()
                .when('status', {
                    is: 'Completed',
                    then: schema => schema.required('Performer is required'),
                    otherwise: schema => schema.nullable().notRequired(),
                }),
        })
    ),
});


const ShiftReportPage = () => {
    useAuthCheck();
    const { reportId } = useParams();
    const navigate = useNavigate();
    const flightsData = useSelector((state) => state.flights);
    const theme = useTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [users, setUsers] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [todosTemplate, setTodosTemplate] = useState([]);
    const [report, setReport] = useState({});
    const [summaryPanelOpen, setSummaryPanelOpen] = useState(true);
    const summaryRef = useRef(null);

    const defaultTodos = useMemo(
        () => todosTemplate.map(t => ({ todo: t.todo, status: 'Pending', performer: null })),
        [todosTemplate]
    );

    const initialValues = useMemo(() => ({
        reportDate: report.reportDate ? toZonedTime(new Date(report.reportDate), 'UTC') : new Date(),
        shift: report.shift || null,
        recipients: report.recipients || [],
        title: report.title || '',
        content: report.content || '',
        includeReportSummary: report._id ? Boolean(report.reportSummary) : true,
        todos: (Array.isArray(report.todos) && report.todos.every(t => t === null))
            ? defaultTodos
            : report.todos?.filter(Boolean) ?? defaultTodos,
    }), [report, defaultTodos]);


    useEffect(() => {
        const fetchData = async () => {
            try {
                setError(null);
                setLoading(true);

                const [usersRes, todosRes, shiftsRes, reportRes] = await Promise.all([
                    api.get('/users/active'),
                    api.get('/todos/active'),
                    api.get('/shifts/active'),
                    reportId
                        ? api.get(`/shiftreports/${reportId}`)
                        : Promise.resolve({ data: {} }),
                ]);

                setUsers(usersRes.data || []);
                setTodosTemplate(todosRes.data || []);
                setShifts(shiftsRes.data || []);
                setReport(reportRes.data || {});
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [reportId]);

    const computeReportSummary = useCallback((reportDate) => {
        if (!reportDate || !Array.isArray(flightsData) || !flightsData.length) return null;

        const targetDate = format(new Date(reportDate), 'yyyy-MM-dd');
        const dayFlights = flightsData.filter(f => {
            const fd = f.flightDate ? new Date(f.flightDate) : null;
            return fd && format(fd, 'yyyy-MM-dd') === targetDate;
        });

        if (!dayFlights.length) return null;

        const cancelled = dayFlights.filter(f =>
            EXCLUDED_FLIGHT_STATUS.some(s => f.flightStatus?.toLowerCase().includes(s.toLowerCase()))
        ).length;

        const diverted = dayFlights.filter(f =>
            DIVERT_RETURN_STATUS.includes(f.flightStatus)
        ).length;

        const opFlights = dayFlights.filter(f =>
            !EXCLUDED_FLIGHT_STATUS.some(s => f.flightStatus?.toLowerCase().includes(s.toLowerCase()))
        );

        const departed = opFlights.filter(f =>
            f.flightStatus?.toLowerCase() !== 'not yet departed' && f.atd
        );
        const arrived = opFlights.filter(f => f.flightStatus?.toLowerCase() === 'arrived');

        const depOtpCount = departed.filter(f => parseDelays(f.flightDepDelays) <= OTP_DELAY_MINUTES).length;
        const arrOtpCount = arrived.filter(f => parseDelays(f.flightArrDelays) <= OTP_DELAY_MINUTES).length;

        const depDelays = departed.map(f => parseDelays(f.flightDepDelays));
        const arrDelays = arrived.map(f => parseDelays(f.flightArrDelays));

        const totalBlockHours = opFlights.reduce((sum, f) => {
            const [h, m] = (f.blockTime || '0:0').split(':').map(Number);
            return sum + (h || 0) + (m || 0) / 60;
        }, 0);

        return {
            totalFlights:    opFlights.length,
            cancelled,
            diverted,
            totalBlockHours: Math.round(totalBlockHours * 10) / 10,
            depOTP:          departed.length ? Math.round((depOtpCount / departed.length) * 100) : null,
            arrOTP:          arrived.length  ? Math.round((arrOtpCount / arrived.length) * 100)  : null,
            avgDelayDep:     depDelays.length ? Math.round(depDelays.reduce((a, b) => a + b, 0) / depDelays.length) : null,
            avgDelayArr:     arrDelays.length ? Math.round(arrDelays.reduce((a, b) => a + b, 0) / arrDelays.length) : null,
        };
    }, [flightsData]);

    const handleFormSubmit = useCallback(async (values, onSubmitProps) => {
        try {
            const method = reportId ? 'put' : 'post';
            const url = reportId
                ? `/shiftreports/update/${reportId}`
                : '/shiftreports/new';

            const reportSummary = values.includeReportSummary
                ? summaryRef.current
                : null;

            const payload = {
                ...values,
                shift: values.shift?._id || null,
                recipients: values.recipients.map(u => u._id),
                todos: values.todos.map((t) => ({
                    ...t,
                    performer: t.performer?._id || null,
                })),
                reportSummary,
                reportDate: values.reportDate ? parseUTCDate(values.reportDate) : null,
            };
            delete payload.includeReportSummary;

            await api({ method, url, data: payload });

            toast.success(reportId
                ? 'Shift report updated successfully.'
                : 'Shift report created successfully.'
            );

            onSubmitProps.resetForm();
            navigate("/shiftreports");
        } catch (error) {
            setError(error);
            toast.error(error.response?.data?.message || 'Failed to submit shift report!');
        }
    }, [navigate, reportId]);


    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title="Shift Report"
                    subtitle={`${loading ? 'Loading...' : reportId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" gap={1}>
                        <StyledButton onClick={() => navigate("/shiftreports")}>
                            <ArrowBackIosNew />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error.message}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} overflow="auto">
                    <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={ADAPTER_LOCALE}
                    >
                        <Formik
                            initialValues={initialValues}
                            enableReinitialize={true}
                            validationSchema={checkoutSchema}
                            onSubmit={handleFormSubmit}
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleBlur,
                                handleChange,
                                handleSubmit,
                                setFieldValue,
                                setFieldTouched,
                            }) => {
                                const reportSummary = computeReportSummary(values.reportDate);
                                summaryRef.current = reportSummary;
                                return (
                                <form onSubmit={handleSubmit}>
                                    <Box
                                        display="grid"
                                        gap={2}
                                        gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                        sx={{ '& > div': { gridColumn: isDesktop ? undefined : 'span 12' } }}
                                    >
                                        <CustomDatePicker
                                            label="Report Date"
                                            name="reportDate"
                                            value={values.reportDate}
                                            onChange={setFieldValue}
                                            touched={touched}
                                            errors={errors}
                                            sx={{ gridColumn: "span 4" }}
                                        />

                                        <Autocomplete
                                            options={shifts}
                                            getOptionLabel={(s) => s.shiftName || s.shiftCode || ''}
                                            value={values.shift}
                                            onChange={(e, newValue) => setFieldValue('shift', newValue)}
                                            onBlur={() => setFieldTouched('shift', true)}
                                            isOptionEqualToValue={(option, value) => option._id === value._id}
                                            renderOption={(props, option) => (
                                                <li {...props} key={option._id}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: option.color || 'grey' }} />
                                                        {option.shiftName || option.shiftCode}
                                                    </Box>
                                                </li>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Shift"
                                                    variant="filled"
                                                    error={!!touched.shift && !!errors.shift}
                                                    helperText={touched.shift && errors.shift}
                                                />
                                            )}
                                            sx={{ gridColumn: 'span 4' }}
                                        />

                                        <Autocomplete
                                            multiple
                                            options={users}
                                            getOptionLabel={(u) => `${u.lastName} ${u.firstName} (${u.email})`}
                                            value={values.recipients}
                                            onChange={(e, newValue) => setFieldValue('recipients', newValue)}
                                            isOptionEqualToValue={(option, value) => option._id === value._id}
                                            renderTags={(selected) =>
                                                selected.length > 0
                                                    ? <Typography variant="body2" noWrap>* {selected.length} selected</Typography>
                                                    : null
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Recipients"
                                                    variant="filled"
                                                    error={!!touched.recipients && !!errors.recipients}
                                                    helperText={touched.recipients && errors.recipients}
                                                />
                                            )}
                                            sx={{ gridColumn: 'span 4' }}
                                            disableCloseOnSelect
                                        />

                                        <TextField
                                            fullWidth
                                            required
                                            variant="filled"
                                            type="text"
                                            label="Title"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.title}
                                            name="title"
                                            error={!!touched.title && !!errors.title}
                                            helperText={touched.title && errors.title}
                                            sx={{ gridColumn: 'span 12' }}
                                        />

                                        <Box sx={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 12' }}>
                                            <TiptapEditor
                                                content={values.content}
                                                onChange={(newContent) => setFieldValue('content', newContent)}
                                                placeholder="Input content ..."
                                            />
                                            {touched.content && errors.content && (
                                                <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                                                    {errors.content}
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Summary Panel */}
                                        <Box sx={{ gridColumn: 'span 12' }}>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                sx={{ cursor: 'pointer', mb: 1 }}
                                                onClick={() => setSummaryPanelOpen(p => !p)}
                                            >
                                                <Typography variant="h5" color={theme.palette.primary.dark}>
                                                    Summary
                                                </Typography>
                                                {summaryPanelOpen ? <ExpandLess /> : <ExpandMore />}
                                            </Box>
                                            <Collapse in={summaryPanelOpen}>
                                                {reportSummary ? (
                                                    <Box>
                                                        <Grid container spacing={1} sx={{ mb: 1 }}>
                                                            {[
                                                                { label: 'Flights', value: reportSummary.totalFlights },
                                                                { label: 'Cancelled', value: reportSummary.cancelled, color: reportSummary.cancelled > 0 ? 'error' : 'default' },
                                                                { label: 'Diverted', value: reportSummary.diverted, color: reportSummary.diverted > 0 ? 'warning' : 'default' },
                                                                { label: 'Block Hrs', value: reportSummary.totalBlockHours },
                                                                { label: 'Dep OTP', value: reportSummary.depOTP != null ? `${reportSummary.depOTP}%` : '-', color: reportSummary.depOTP >= 85 ? 'success' : reportSummary.depOTP >= 70 ? 'warning' : 'error' },
                                                                { label: 'Arr OTP', value: reportSummary.arrOTP != null ? `${reportSummary.arrOTP}%` : '-', color: reportSummary.arrOTP >= 85 ? 'success' : reportSummary.arrOTP >= 70 ? 'warning' : 'error' },
                                                                { label: 'Avg Dep Delay', value: reportSummary.avgDelayDep != null ? `${reportSummary.avgDelayDep} min` : '-' },
                                                                { label: 'Avg Arr Delay', value: reportSummary.avgDelayArr != null ? `${reportSummary.avgDelayArr} min` : '-' },
                                                            ].map(kpi => (
                                                                <Grid item key={kpi.label}>
                                                                    <Chip
                                                                        label={`${kpi.label}: ${kpi.value ?? '-'}`}
                                                                        color={kpi.color || 'default'}
                                                                        variant="outlined"
                                                                        size="small"
                                                                    />
                                                                </Grid>
                                                            ))}
                                                        </Grid>
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    checked={values.includeReportSummary}
                                                                    onChange={(e) => setFieldValue('includeReportSummary', e.target.checked)}
                                                                    color="primary"
                                                                />
                                                            }
                                                            label="Include summary in report"
                                                        />
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="textSecondary">
                                                        No data available for this date.
                                                    </Typography>
                                                )}
                                            </Collapse>
                                        </Box>

                                        <FieldArray name="todos">
                                            {({ remove, push }) => (
                                                <Box sx={{ gridColumn: 'span 12' }}>
                                                    <Typography variant="h5" color={theme.palette.primary.dark} mb={1}>
                                                        Todos Checklist
                                                    </Typography>
                                                    {values.todos?.map((todo, index) => (
                                                        <Box key={index}>
                                                        <Box
                                                            display="flex"
                                                            flexWrap={isMobile ? 'wrap' : 'nowrap'}
                                                            alignItems="center"
                                                            gap={1}
                                                            mb={isMobile ? 1 : 2}
                                                        >
                                                            <TextField
                                                                label="Todo"
                                                                name={`todos[${index}].todo`}
                                                                value={todo.todo}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                error={touched.todos?.[index]?.todo && !!errors.todos?.[index]?.todo}
                                                                helperText={touched.todos?.[index]?.todo && errors.todos?.[index]?.todo}
                                                                variant="filled"
                                                                sx={{ flex: isMobile ? '1 1 100%' : 6 }}
                                                            />
                                                            <TextField
                                                                variant="filled"
                                                                select
                                                                label="Status"
                                                                name={`todos[${index}].status`}
                                                                value={todo.status || 'Pending'}
                                                                error={touched.todos?.[index]?.status && !!errors.todos?.[index]?.status}
                                                                helperText={touched.todos?.[index]?.status && errors.todos?.[index]?.status}
                                                                onChange={(e) => {
                                                                    handleChange(e);
                                                                    if (e.target.value === 'Completed') {
                                                                        setFieldTouched(`todos[${index}].performer`, true);
                                                                    }
                                                                    if (e.target.value === 'Pending') {
                                                                        setFieldValue(`todos[${index}].performer`, null);
                                                                    }
                                                                }}
                                                                onBlur={handleBlur}
                                                                sx={{ flex: isMobile ? '1 1 auto' : 2 }}
                                                            >
                                                                {["Pending", "Completed"].map((opt) => (
                                                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                                                ))}
                                                            </TextField>
                                                            <Autocomplete
                                                                options={users}
                                                                getOptionLabel={(u) => u.email}
                                                                value={todo.performer}
                                                                onChange={(e, newValue) => setFieldValue(`todos[${index}].performer`, newValue)}
                                                                onBlur={() => setFieldTouched(`todos[${index}].performer`, true)}
                                                                isOptionEqualToValue={(option, value) => option._id === value._id}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label="Performer"
                                                                        variant="filled"
                                                                        error={touched.todos?.[index]?.performer && !!errors.todos?.[index]?.performer}
                                                                        helperText={touched.todos?.[index]?.performer && errors.todos?.[index]?.performer}
                                                                    />
                                                                )}
                                                                sx={{ flex: isMobile ? '2 1 auto' : 4 }}
                                                            />
                                                            <IconButton color="error" onClick={() => remove(index)}>
                                                                <RemoveCircle />
                                                            </IconButton>
                                                        </Box>
                                                        {isMobile && index < values.todos.length - 1 && (
                                                            <Divider sx={{ my: 1 }} />
                                                        )}
                                                        </Box>
                                                    ))}
                                                    <Box>
                                                        <IconButton
                                                            size="large"
                                                            color="primary"
                                                            onClick={() => push({ todo: '', status: 'Pending', performer: null })}
                                                        >
                                                            <AddCircle />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                            )}
                                        </FieldArray>

                                        <Button
                                            fullWidth
                                            type="submit"
                                            variant="contained"
                                            color="secondary"
                                            sx={{ gridColumn: "span 12", height: 42 }}
                                        >
                                            {reportId ? "Update" : "Create"}
                                        </Button>
                                    </Box>
                                </form>
                                );
                            }}
                        </Formik>
                    </LocalizationProvider>
                </Box>
            )}
        </Box>
    );
};

export default ShiftReportPage;
